import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Loader2, MailCheck, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/checkout/sucesso")({
  head: () => ({
    meta: [
      { title: "Acesso liberado — Plataforma 101" },
      { name: "description", content: "Seu acesso à Plataforma 101 foi liberado." },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({ id: typeof s.id === "string" ? s.id : undefined }),
  component: SuccessPage,
});

function SuccessPage() {
  const { id } = Route.useSearch();
  const [paid, setPaid] = useState<boolean | null>(null);

  useEffect(() => {
    if (!id) { setPaid(null); return; }
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const r = await fetch(`/api/public/checkout/status?id=${encodeURIComponent(id)}`);
        const j = await r.json();
        if (!stopped && j?.paid) { setPaid(true); return true; }
      } catch {}
      if (attempts > 20 && !stopped) setPaid(false);
      return false;
    };
    tick();
    const iv = window.setInterval(async () => {
      const done = await tick();
      if (done) window.clearInterval(iv);
    }, 4000);
    return () => { stopped = true; window.clearInterval(iv); };
  }, [id]);

  return (
    <div className="min-h-screen bg-[#F6F1E8]">
      <header className="border-b border-black/5 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#0C1B33] text-[10px] font-black tracking-widest text-white">101</div>
            <span className="font-[Manrope] text-sm font-extrabold tracking-tight text-[#0C1B33]">Projeto 101</span>
          </Link>
          <div className="inline-flex items-center gap-1 text-xs text-[#5C6675]"><ShieldCheck className="h-3.5 w-3.5" /> Compra segura</div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <div className="rounded-2xl border border-black/5 bg-white p-8 shadow-sm">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#1E7A5C] text-white">
            {paid ? <Check className="h-7 w-7" /> : <Loader2 className="h-6 w-6 animate-spin" />}
          </div>
          <h1 className="mt-5 text-center font-[Manrope] text-2xl font-extrabold tracking-tight text-[#0C1B33] sm:text-3xl">
            {paid ? "Seu acesso foi liberado." : "Estamos confirmando seu pagamento…"}
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-[#5C6675]">
            {paid
              ? "Bem-vindo à Plataforma 101. Enviamos para o seu e-mail as instruções para criar sua senha — agora você pode acessar todo o conteúdo diretamente da sua área exclusiva."
              : "Assim que o processador confirmar o pagamento, seu acesso à Plataforma 101 será liberado automaticamente. Você pode fechar esta página com segurança."}
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { icon: <MailCheck className="h-4 w-4 text-[#1E7A5C]" />, t: "E-mail de boas-vindas", d: "Enviado com o link para criar sua senha." },
              { icon: <ShieldCheck className="h-4 w-4 text-[#1E7A5C]" />, t: "Garantia de 7 dias", d: "Se não fizer sentido, devolvemos 100%." },
              { icon: <Check className="h-4 w-4 text-[#1E7A5C]" />, t: "Acesso vitalício", d: "Sem mensalidade. Atualizações incluídas." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-black/5 bg-[#F6F1E8] p-4">
                <div className="flex items-center gap-2 text-sm font-bold text-[#0C1B33]">{c.icon}{c.t}</div>
                <div className="mt-1 text-xs text-[#5C6675]">{c.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Button asChild className="h-12 bg-[#0C1B33] px-6 text-white hover:bg-[#0C1B33]/90">
              <Link to="/auth">Entrar na Plataforma <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[#5C6675]">
          Precisa de ajuda? Fale com nosso suporte pelo e-mail informado no seu comprovante.
        </p>
      </main>
    </div>
  );
}
